import { Panel } from "@kunduz/ui";
import Sticky from "../../../../common/components/sticky";
import { trackEvent } from "@kunduz/utility-analytics";
import {
  calculateSlugForQuestion,
  cleanWhitespace,
} from "../../../../common/services/utilityService";
import {
  QuestionImage,
  QuestionText,
  QuestionTitle,
} from "@kunduz/feature-question-indexing";
import { useTranslation } from "next-i18next";
import { NextSeo, QAPageJsonLd } from "next-seo";
import Head from "next/head";
import { ReactElement, useEffect } from "react";
import QuestionAnswerLoader from "../../components/answer/questionAnswerLoader";
import { QIBreadcrumb } from "../../components/breadcrumb";
import PrevNextButtons from "../../components/prevNextButtons";
import styles from "./pageStyles.module.scss";
import { QuestionIndexing } from "@kunduz/data-access-qi-api";
import InstallComponent from "../../../../common/components/installComponent/loader";
import QuestionSideCtaLoader, {
  SideCTAModes,
} from "../../../../common/components/sideCta";
import { LastQuestions } from "../../components/lastQuestions/lastQuestions";
import { LecturesVideos } from "../../components/lecturesVideos/lecturesVideos";
import Link from "next/link";
import { getQIUrl } from "@kunduz/utility-common";
import {
  BlogQIMobileCta,
  WebsiteTrFooter,
  DynamicHeader,
  HeaderBannerWithContext,
} from "@kunduz/feature-landing-pages";
import { type IContent } from "@kunduz/data-access-cms-api";

export const SingleQuestionPage = (props: Props): ReactElement => {
  const { t } = useTranslation("questionIndexing");

  const title = t("question_detail_meta_seo_title", {
    seoTitle: cleanWhitespace(props.question?.seo_title),
  });
  const description = t("question_detail_meta_description", {
    meta: cleanWhitespace(props.question.seo_meta).slice(0, 110),
  });
  const url = getQIUrl({
    lang: props.lang,
    domain: process.env.NEXT_PUBLIC_DOMAIN_PREFIX,
    courseSlug: props.question.course.slug,
    subjectSlug: props.question.subject.slug,
    questionSlug: props.question.slug,
    questionId: props.question.id,
  });

  function prepareQuestionImageUrl(_url: string) {
    return `${_url}?h=${512}`;
  }

  useEffect(() => {
    sendPageViewEvent();
  }, []);

  function sendPageViewEvent() {
    trackEvent("Question Indexing Question Detail Page Visited", {
      course: props.question.course?.name,
      subject: props.question.subject?.name,
      page_slug: calculateSlugForQuestion(
        props.question.course?.slug,
        props.question.subject?.slug,
        props.question.slug,
        props.question.id,
      ),
      initial_answer_credit: t("question_indexing_answer_view_credit"),
      is_edited: props.question.is_edited,
      is_unlisted_question:
        props.question?.status == QuestionIndexing.TrQiQuestionStatus.UNLISTED,
    });
  }

  const sendInstallComponentClickedEvent = (
    buttonType: "app_store" | "google_play",
  ) => {
    trackEvent("Question Indexing Install CTA Clicked", {
      button_type: buttonType,
      source: "under_show_answer",
    });
  };

  /** prints QAPage Structured Content */
  function printStructuredContent() {
    if (!props.question?.structured_data?.acceptedAnswer?.text) return;

    return (
      <QAPageJsonLd
        mainEntity={{
          name: props.question?.structured_data?.name,
          url: url,
          text: props.question?.content,
          answerCount: 1,
          image: props.question?.question_image,
          dateCreated: props.question?.structured_data?.dateCreated,
          author: {
            name: props.question?.structured_data?.author,
          },
          acceptedAnswer: {
            text: t("question_indexing_qa_page_accepted_answer_text"),
            dateCreated:
              props.question?.structured_data?.acceptedAnswer?.dateCreated,
            url: url,
            author: {
              name: props.question?.structured_data?.author,
            },
          },
        }}
      />
    );
  }

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://media.kunduz.com" />
        <link rel="dns-prefetch" href="https://media.kunduz.com" />
        <link
          rel="preload"
          href={prepareQuestionImageUrl(props.question.question_image)}
          as="image"
        />
      </Head>

      <NextSeo
        title={title}
        description={description}
        openGraph={{
          url: props.url,
          images: [
            {
              url: prepareQuestionImageUrl(props.question.question_image),
              height: 512,
              alt: t("question_detail_meta_description", {
                meta: cleanWhitespace(props.question.seo_meta).slice(0, 110),
              }),
            },
          ],
          site_name: t("course_list_meta_title"),
        }}
        twitter={{
          handle: props.lang == "en" ? "@askkunduz" : "@kunduzasor",
          cardType: "summary_large_image",
        }}
        canonical={
          props.question.status == QuestionIndexing.TrQiQuestionStatus.UNLISTED
            ? undefined
            : url
        }
        noindex={
          props.question.status == QuestionIndexing.TrQiQuestionStatus.UNLISTED
        }
        robotsProps={{
          maxImagePreview: "large",
        }}
      />
      {printStructuredContent()}

      {props.headerBanner && (
        <HeaderBannerWithContext
          label={props.headerBanner.content}
          buttonLabel={props.headerBanner.cta_title}
          url={props.headerBanner.cta_link}
        />
      )}

      <DynamicHeader
        lang={props.lang}
        headerType="student"
        headerItems={props.headerItems}
      />

      <BlogQIMobileCta />

      <div className={styles.pageContainer}>
        <div className={styles.breadcrumb}>
          <QIBreadcrumb
            question={props.question}
            lang={props.lang}
            linkAs={Link}
          />
        </div>

        <main className={styles.content}>
          <Panel>
            <QuestionTitle
              label={t("question_indexing_question_title")}
              title={props.question.title}
            />
            <QuestionImage
              imageUrl={props.question?.question_image}
              imageAlt={props.question?.seo_title}
            />
            <QuestionText questionText={props.question.content} />
          </Panel>

          <PrevNextButtons
            lang={props.lang}
            subjectSlug={props.question.subject?.slug}
            linkAs={Link}
          />

          <QuestionAnswerLoader
            lang={props.lang}
            question={props.question}
            url={props.url}
            isAuth={props.isAuth}
          />

          <InstallComponent
            type="downloadApp"
            isMobile={props.isMobile}
            sendInstallComponentClickedEvent={sendInstallComponentClickedEvent}
          />

          <LastQuestions
            courseSlug={props.question.course.slug}
            id={props.question.id}
            isMobile={props.isMobile}
            subjectName={props.question.subject.name}
            lang={props.lang}
            subjectSlug={props.question.subject.slug}
            linkAs={Link}
          />

          <LecturesVideos
            lang={props.lang}
            id={props.question.subject.remote_id}
          />
        </main>

        <aside className={styles.sidebar} suppressHydrationWarning>
          <Sticky top={100}>
            <QuestionSideCtaLoader
              isMobile={props.isMobile}
              lang={props.lang}
              mode={SideCTAModes.QI}
              translationFile="questionIndexing"
            />
          </Sticky>
        </aside>
      </div>

      <WebsiteTrFooter />
    </>
  );
};

interface Props {
  question: QuestionIndexing.TrQiQuestion;
  lang: "tr" | "en";
  url: string;
  isAuth: boolean;
  isMobile: boolean;
  headerItems: IContent.HeaderItemsResponse;
  headerBanner?: IContent.HeaderBannerResponse;
}
